/**
 * KKG Block „Statement Lines" (blocks/statement-lines) – Scroll-Choreografie
 *
 * Große Textzeilen („Lines") werden beim Scrollen nacheinander aufgebaut,
 * magnetisch am Magnetpunkt gehalten und wieder gelöst, bevor die nächste
 * Line hereinfliegt. Figma: BBP Screendesign, Node 110-2103.
 *
 * Technik
 *  - Die Bühne (.statement-lines__stage, 100vh) wird per ScrollTrigger gepinnt;
 *    Scrollweg = Anzahl Lines × scroll_per_line (vh, ACF).
 *  - EINE gescrubte Timeline verteilt den Scrollfortschritt auf Segmente pro
 *    Line:  Build (clip-path von links nach rechts + weiches Einfliegen) →
 *    Hold (Line liegt unbewegt am Magnetpunkt) → Release (Line löst sich nach
 *    oben, blendet aus). Release und Build der nächsten Line überlappen sich
 *    (TRANSITION_OVERLAP) zu einer Überblendung – ohne leere Bühne dazwischen.
 *  - Die ERSTE Line baut sich schon auf, während die Bühne ins Bild scrollt
 *    (eigene gescrubte Intro-Timeline vor dem Pin). Beim Einrasten des Pins
 *    liegt sie bereits am Magnetpunkt, die Pin-Timeline beginnt mit ihrem Hold.
 *  - Magnetismus: kommt der Scroll außerhalb einer Hold-Zone zur Ruhe, zieht
 *    der Block die Seite an den nächsten Magnetpunkt in Scrollrichtung (eigene
 *    Logik statt ScrollTrigger-`snap`, das sich mit dem seitenweiten
 *    ScrollSmoother verhakt). Ein geglätteter Scrub sorgt für das physische
 *    Nachlaufen. Jede Nutzereingabe (Wheel, Touch, Tastatur) übersteuert den
 *    Magneten sofort.
 *  - Vollständig reversibel (Scrollfortschritt = Zustand), Resize-sicher
 *    (function-based values + invalidateOnRefresh), nur transform/opacity/
 *    clip-path, kein eigener Scroll-Listener.
 *  - prefers-reduced-motion: keine Animation, statisches Figma-Layout.
 *
 * Desktop vs. Touch (siehe „nativeScroll" in setupBlock)
 *  - Desktop: ScrollSmoother glättet (smooth: 1.5) und verschiebt #smooth-content
 *    per transform. Pin per transform, Magnet über smoother.scrollTo() – die
 *    Glättung des Smoothers liefert die weiche Bewegung.
 *  - Touch (ScrollSmoother ohne smoothTouch → smooth = 0): Die Seite scrollt
 *    nativ mit Momentum, ScrollTrigger hängt aber weiter am #smooth-wrapper-
 *    Proxy. Zwei Dinge ruckelten dadurch:
 *      1. Pin per transform: wird pro Scroll-Event im JS nachgezogen, während
 *         der Compositor nativ scrollt → die Bühne läuft einen Frame hinterher
 *         und zittert (besonders im iOS-Momentum). Lösung: pinType "fixed".
 *      2. Magnet: smoother.scrollTo() setzt ohne Glättung die Scrollposition
 *         hart (window.scrollTo), und der Idle-Timer feuerte noch während des
 *         auslaufenden Momentums → Sprung gegen das native Scrolling. Lösung:
 *         Magnet erst nach ScrollTriggers „scrollEnd" (kein Scroll-Event mehr,
 *         kein Finger auf dem Display) und als EIN interpolierter GSAP-Tween
 *         (ScrollToPlugin) auf die Scrollposition, den jede Berührung sofort
 *         beendet. Während des Touch-Scrollens greift der Magnet nie ein.
 *
 * Markup: blocks/statement-lines/render.php
 * Styles: src/sass/theme/_kkg_components.scss (Abschnitt „Statement Lines")
 */
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollSmoother } from "gsap/ScrollSmoother";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";

gsap.registerPlugin(ScrollTrigger, ScrollSmoother, ScrollToPlugin);

// Nachlauf der Animation hinter der Scrollposition (Sekunden).
const SCRUB_SMOOTHING = 0.9;

// Intro der ersten Line vor dem Pin: Position der Block-Oberkante im Viewport,
// bei der der Build beginnt bzw. abgeschlossen ist. Die Line liegt mittig in
// der Bühne, sichtbar wird sie also ab ca. „top 50%".
const INTRO_START = "top 62%";
const INTRO_END = "top 6%";

// Überblendung zwischen zwei Lines: Anteil, um den sich Release der alten und
// Build der neuen Line überlappen (bezogen auf den kürzeren der beiden Anteile).
const TRANSITION_OVERLAP = 0.45;

// Auf Mobil kürzerer Scrollweg pro Line (Faktor) – keine überlangen Strecken.
const MOBILE_SCROLL_FACTOR = 0.65;
const MOBILE_MAX_WIDTH = 768;

// Untergrenze pro Line in Viewport-Höhen.
const MIN_SEGMENT_VH = 0.6;

// Magnet (Desktop/ScrollSmoother): Wartezeit nach dem letzten Scroll-Update (ms)
// und Geschwindigkeit (px/s), unterhalb der der Scroll als „zur Ruhe gekommen" gilt.
const MAGNET_IDLE_MS = 140;
const MAGNET_MAX_VELOCITY = 60;

// Magnet (Touch/nativer Scroll): Dauer des Tweens zum Magnetpunkt (s). Der
// Zeitpunkt kommt von ScrollTriggers „scrollEnd" (≈200–450 ms nach dem letzten
// Scroll-Event), deshalb hier kein eigener Idle-Timer.
const MAGNET_TOUCH_DURATION = 0.8;

// Toleranz um die Hold-Zone, innerhalb der nicht nachgezogen wird.
const MAGNET_TOLERANCE = 0.004;

/**
 * Initialisiert alle Statement-Lines-Blöcke der Seite.
 */
export function initStatementLines() {
    document.querySelectorAll("[data-kkg-lines]").forEach(setupBlock);
}

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}

function setupBlock(block) {
    const stage = block.querySelector("[data-kkg-lines-stage]");
    const lines = Array.from(block.querySelectorAll("[data-kkg-line]"));
    const aside = block.querySelector("[data-kkg-lines-aside]"); // Badge + Randnotiz, rechts unten
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (!stage || lines.length === 0) {
        return null;
    }

    // Editor-Vorschau oder reduzierte Bewegung: statisches Layout, keine Animation.
    if (reduceMotion || block.classList.contains("is-static")) {
        block.classList.add("is-static");
        return null;
    }

    block.classList.add("is-animated");

    // Scrollt die Seite nativ? Maßgeblich ist, was ScrollSmoother tatsächlich tut:
    // smooth() liefert 0, wenn er nichts glättet (Touch-Geräte ohne smoothTouch,
    // erkannt über „(hover: none), (pointer: coarse)" – keine UA-Erkennung) oder
    // wenn gar kein Smoother existiert. Dann übernimmt der Browser das Scrollen
    // samt Momentum, und Pin wie Magnet müssen darauf Rücksicht nehmen.
    const smoother = ScrollSmoother.get();
    const nativeScroll = !smoother || !smoother.smooth();

    const count = lines.length;
    const scrollVh = clamp(parseInt(block.dataset.kkgLinesScroll, 10) || 100, 40, 300) / 100;
    const holdShare = clamp((parseInt(block.dataset.kkgLinesHold, 10) || 40) / 100, 0.1, 0.8);

    // Aufteilung eines Segments (Summe = 1): Build | Hold | Release
    const buildShare = (1 - holdShare) * 0.55;
    const releaseShare = 1 - holdShare - buildShare;

    // Überlappung von Release und folgendem Build (in Segment-Einheiten).
    const overlap = Math.min(buildShare, releaseShare) * TRANSITION_OVERLAP;

    // Gesamtdauer in Segmenten: der Build der ersten Line läuft vor dem Pin
    // (Intro-Timeline), die letzte Line wird nicht mehr gelöst – sie bleibt bis
    // zum Ende des Pins am Magnetpunkt. Zeit 0 der Pin-Timeline = Hold-Beginn
    // der ersten Line, alle Segmente sind um buildShare nach vorn verschoben.
    const totalDuration = (count - 1) + holdShare;
    const holdStart = (i) => i; // Hold-Beginn der Line i auf der Pin-Timeline

    // Scrollweg pro Segment in px – aus der echten Viewport-Höhe.
    const segmentPx = () => {
        const vh = window.innerHeight;
        const factor = window.innerWidth < MOBILE_MAX_WIDTH ? MOBILE_SCROLL_FACTOR : 1;
        return Math.max(vh * MIN_SEGMENT_VH, vh * scrollVh * factor);
    };

    // Hold-Mittelpunkte (Magnetpunkte) als Fortschritt 0–1.
    const magnetPoints = lines.map((_, i) => (holdStart(i) + holdShare / 2) / totalDuration);

    // Liegt der Fortschritt in einer Hold-Zone (Line ruht am Magnetpunkt)?
    const inHoldZone = (progress) => lines.some((_, i) => {
        const from = holdStart(i) / totalDuration - MAGNET_TOLERANCE;
        const to = (holdStart(i) + holdShare) / totalDuration + MAGNET_TOLERANCE;
        return progress >= from && progress <= to;
    });

    // Nächster Magnetpunkt in Scrollrichtung (sonst der nächstgelegene).
    const nextMagnet = (progress, direction) => {
        const ahead = magnetPoints.filter((m) => (direction >= 0 ? m > progress : m < progress));
        if (ahead.length) {
            return direction >= 0 ? ahead[0] : ahead[ahead.length - 1];
        }
        return magnetPoints.reduce((a, b) => (Math.abs(b - progress) < Math.abs(a - progress) ? b : a));
    };

    // Magnet: nach Scroll-Stillstand außerhalb einer Hold-Zone zum Magnetpunkt ziehen.
    // Es gibt immer nur EINE laufende Instanz (Timer bzw. Tween), die die
    // Scrollposition anfasst – nie zwei Systeme gleichzeitig.
    let idleTimer = null;
    let magnetTween = null;

    const killMagnet = () => {
        clearTimeout(idleTimer);
        idleTimer = null;
        if (magnetTween) {
            magnetTween.kill();
            magnetTween = null;
        }
    };

    const magnetize = (self) => {
        if (!self.isActive || inHoldZone(self.progress)) {
            return;
        }
        // Desktop: der Smoother läuft noch nach – erst ziehen, wenn er (fast) steht.
        if (!nativeScroll && Math.abs(self.getVelocity()) > MAGNET_MAX_VELOCITY) {
            scheduleMagnet(self);
            return;
        }
        const target = nextMagnet(self.progress, self.direction);
        const y = self.start + target * (self.end - self.start);

        if (nativeScroll) {
            // Touch: smoother.scrollTo() würde hier hart springen (keine Glättung).
            // Stattdessen ein interpolierter Tween auf die Scrollposition; er
            // startet erst, wenn das native Momentum ausgelaufen ist (scrollEnd),
            // und stirbt bei jeder Berührung (killMagnet) bzw. per autoKill, sobald
            // die Position von außen verändert wird.
            killMagnet();
            magnetTween = gsap.to(window, {
                scrollTo: { y: y, autoKill: true },
                duration: MAGNET_TOUCH_DURATION,
                ease: "expo.out", // wie das Easing des ScrollSmoothers
                overwrite: "auto",
                onComplete: () => { magnetTween = null; },
            });
            return;
        }

        smoother.scrollTo(y, true); // weich, mit der Glättung des Smoothers
    };
    const scheduleMagnet = (self) => {
        clearTimeout(idleTimer);
        idleTimer = setTimeout(() => magnetize(self), MAGNET_IDLE_MS);
    };

    // Nutzereingabe übersteuert den Magneten (kein Scroll-Listener, nur Input).
    ["wheel", "touchstart", "pointerdown", "keydown"].forEach((type) => {
        window.addEventListener(type, killMagnet, { passive: true });
    });

    // Einflug-/Ausflug-Wege aus den echten Elementgrößen.
    const flyIn = (line) => Math.round(line.getBoundingClientRect().height * 0.45);
    const flyOut = (line) => Math.round(line.getBoundingClientRect().height * 0.6);
    const shiftX = () => Math.round(window.innerWidth * 0.03);

    // Startzustand vor dem ersten Tick: nur der Magnetpunkt ist leer.
    gsap.set(lines, { opacity: 0 });
    if (aside) {
        gsap.set(aside, { opacity: 0 });
    }

    let activeIndex = -1;
    const setActive = (progress) => {
        const position = progress * totalDuration + buildShare; // Segmentraster inkl. Build
        let index = clamp(Math.floor(position), 0, count - 1);
        // Innerhalb des Release-Anteils zählt die Line bereits als „gelöst".
        const local = position - index;
        if (index < count - 1 && local > buildShare + holdShare) {
            index = -1;
        }
        if (index === activeIndex) {
            return;
        }
        if (activeIndex >= 0) {
            lines[activeIndex].classList.remove("is-active");
        }
        activeIndex = index;
        if (index >= 0) {
            lines[index].classList.add("is-active");
        }
    };

    // Build einer Line: baut sich von links nach rechts auf (clip-path), steigt
    // dabei weich an den Magnetpunkt und blendet ein. Kein Overshoot – gescrubt
    // wirkt das Zurückfedern wie ein Ruckeln.
    const addBuild = (tl, line, position, duration) => {
        const text = line.querySelector("[data-kkg-line-text]") || line;
        tl.fromTo(
            text,
            { clipPath: "inset(-15% 100% -15% 0)" },
            { clipPath: "inset(-15% 0% -15% 0)", duration: duration, ease: "power2.inOut", immediateRender: false },
            position
        );
        tl.fromTo(
            line,
            { y: () => flyIn(line), x: () => -shiftX() },
            { y: 0, x: 0, duration: duration, ease: "power3.out", immediateRender: false },
            position
        );
        tl.fromTo(
            line,
            { opacity: 0 },
            { opacity: 1, duration: duration * 0.7, ease: "sine.inOut", immediateRender: false },
            position
        );
    };

    // Intro vor dem Pin: die erste Line (und Badge + Randnotiz) bauen sich auf,
    // während die Bühne ins Bild scrollt. Vor der Pin-Timeline angelegt, damit
    // sie beim Refresh zuerst rendert.
    const intro = gsap.timeline({
        defaults: { ease: "none" },
        scrollTrigger: {
            trigger: block,
            start: INTRO_START,
            end: INTRO_END,
            scrub: SCRUB_SMOOTHING,
            invalidateOnRefresh: true,
        },
    });
    addBuild(intro, lines[0], 0, 1);
    if (aside) {
        // Bleibt danach (innerhalb der gepinnten Bühne) stehen, bis alle Lines durch sind.
        intro.fromTo(
            aside,
            { y: 24, opacity: 0 },
            { y: 0, opacity: 1, duration: 0.6, ease: "power2.out", immediateRender: false },
            0.4
        );
    }

    const timeline = gsap.timeline({
        defaults: { ease: "none" },
        scrollTrigger: {
            trigger: stage,
            start: "top top",
            end: () => "+=" + Math.round(segmentPx() * totalDuration),
            pin: stage,
            pinSpacing: true,
            anticipatePin: 1,
            // Scroller ist der #smooth-wrapper-Proxy des ScrollSmoothers, daher
            // würde ScrollTrigger immer per transform pinnen. Bei nativem Scroll
            // (Touch) läuft das dem Compositor hinterher und ruckelt – dort
            // position: fixed, das der Browser selbst synchron hält.
            pinType: nativeScroll ? "fixed" : "transform",
            scrub: SCRUB_SMOOTHING,
            invalidateOnRefresh: true,
            onUpdate: (self) => {
                setActive(self.progress);
                // Touch: kein Timer pro Scroll-Event – der Magnet hängt an „scrollEnd".
                if (!nativeScroll) {
                    scheduleMagnet(self);
                }
            },
            onRefresh: (self) => setActive(self.progress),
            onLeave: killMagnet,
            onLeaveBack: killMagnet,
        },
    });

    // Touch: Magnet erst, wenn ScrollTrigger das Scrollende meldet (kein Scroll-
    // Event seit >200 ms, kein Pointer/Finger unten). So greift er nie in das
    // native Momentum-Scrolling ein. Nach dem eigenen Tween feuert scrollEnd
    // erneut, dann liegt der Fortschritt in der Hold-Zone und nichts passiert.
    if (nativeScroll) {
        ScrollTrigger.addEventListener("scrollEnd", () => magnetize(timeline.scrollTrigger));
    }

    lines.forEach((line, i) => {
        // 1) Build (ab der zweiten Line; die erste kommt über das Intro). Beginnt
        //    schon während des Release der vorherigen Line → Überblendung.
        if (i > 0) {
            addBuild(timeline, line, holdStart(i) - buildShare - overlap, buildShare + overlap);
        }

        // 2) Hold: keine Tweens – die Line liegt am Magnetpunkt (Snap-Ziel).

        // 3) Release: Line löst sich nach oben und blendet aus (nicht bei der
        //    letzten Line). fromTo, damit der Start unabhängig vom Intro feststeht.
        if (i < count - 1) {
            const releaseAt = holdStart(i) + holdShare;
            const releaseDuration = releaseShare + overlap;
            timeline.fromTo(
                line,
                { y: 0 },
                { y: () => -flyOut(line), duration: releaseDuration, ease: "power2.inOut", immediateRender: false },
                releaseAt
            );
            timeline.fromTo(
                line,
                { opacity: 1 },
                { opacity: 0, duration: releaseDuration * 0.8, ease: "sine.inOut", immediateRender: false },
                releaseAt
            );
        }
    });

    // Timeline exakt auf die Gesamtdauer auffüllen: die letzte Line hat keinen
    // Release-Tween, ihr Hold-Anteil muss trotzdem Scrollweg bekommen. Sonst
    // endet die Timeline mit dem letzten Build und Scroll -> Phase verschiebt sich.
    timeline.to({}, { duration: totalDuration - timeline.duration() }, timeline.duration());

    // Debug-Zugriff (Konsole: kkgLines[0].trigger.progress)
    window.kkgLines = window.kkgLines || [];
    window.kkgLines.push({ block, timeline, intro, trigger: timeline.scrollTrigger, magnetPoints, totalDuration, nativeScroll });

    // Nach Webfont-Laden neu vermessen (Line-Höhen ändern sich).
    if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => ScrollTrigger.refresh());
    }

    return timeline;
}
